/* =============================================================================
 * IntegrationHealthBanner — §0.4
 * -----------------------------------------------------------------------------
 * Shows a "reconnect" prompt when an athlete's Gmail/X/YouTube OAuth connection
 * has gone dead (needs_reauth). Without this, a revoked connection silently
 * fails every coach email — the row still exists so the old connected/not check
 * reads it as fine. Reads the main-API health endpoint (reachable everywhere)
 * and re-uses the existing useSocialConnect popup for the actual reconnect.
 * ========================================================================== */
import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle } from 'react-feather';
import { integrationHealthAPI, IntegrationHealth } from '../services/api';
import { useSocialConnect, SocialPlatform } from '../hooks/useSocialConnect';

type Provider = 'gmail' | 'x' | 'youtube';

const PROVIDER_LABEL: Record<Provider, string> = { gmail: 'Gmail', x: 'X', youtube: 'YouTube' };
const TO_SOCIAL_PLATFORM: Record<Provider, SocialPlatform> = {
  gmail: 'gmail',
  x: 'twitter',
  youtube: 'youtube',
};

interface Props {
  athleteId: string | null;
  token: string | null;
  /** Which providers to warn about. Defaults to Gmail (the coach-email channel). */
  providers?: Provider[];
  /** Fired after a reconnect attempt completes, so the parent can refresh state. */
  onReconnected?: () => void;
}

const IntegrationHealthBanner: React.FC<Props> = ({
  athleteId,
  token,
  providers = ['gmail'],
  onReconnected,
}) => {
  const [dead, setDead] = useState<IntegrationHealth[]>([]);

  const load = useCallback(async () => {
    if (!athleteId || !token) return;
    try {
      const res = await integrationHealthAPI.getHealth(token, athleteId);
      setDead(
        (res.integrations || []).filter(
          (i) => i.needs_reauth && providers.includes(i.provider as Provider)
        )
      );
    } catch {
      // Health is best-effort — never block the surrounding UI on it.
    }
  }, [athleteId, token, providers]);

  const { connect, connecting } = useSocialConnect({
    athleteId: athleteId || '',
    onConnected: () => {
      load();
      onReconnected?.();
    },
  });

  useEffect(() => {
    load();
  }, [load]);

  if (dead.length === 0) return null;

  const provider = dead[0].provider as Provider;
  const label = PROVIDER_LABEL[provider] || provider;
  const isConnecting = connecting === TO_SOCIAL_PLATFORM[provider];

  return (
    <div style={styles.banner} role="alert">
      <AlertTriangle size={18} style={{ color: '#F59E0B', flexShrink: 0 }} />
      <div style={styles.text}>
        {dead[0].reauth_reason === 'gmail_send_permission' ? (
          <>
            <strong>{label} can't send yet.</strong> Reconnect it and allow every permission Google asks for.
          </>
        ) : (
          <>
            <strong>{label} disconnected.</strong> Reconnect it so your messages to coaches keep sending.
          </>
        )}
      </div>
      <button
        type="button"
        style={{ ...styles.btn, ...(isConnecting ? styles.btnDisabled : {}) }}
        disabled={isConnecting}
        onClick={() => connect(TO_SOCIAL_PLATFORM[provider])}
      >
        {isConnecting ? 'Reconnecting…' : `Reconnect ${label}`}
      </button>
    </div>
  );
};

const styles: { [k: string]: React.CSSProperties } = {
  banner: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '10px 14px',
    background: '#FFFAEB',
    border: '1px solid #FEDF89',
    borderRadius: 10,
    margin: '0 0 12px',
    fontFamily: 'Manrope, system-ui, sans-serif',
  },
  text: { flex: 1, fontSize: 13.5, color: '#93370D', lineHeight: 1.35 },
  btn: {
    flexShrink: 0,
    fontSize: 13,
    fontWeight: 700,
    color: '#fff',
    background: '#F9550D',
    border: 'none',
    borderRadius: 8,
    padding: '8px 14px',
    cursor: 'pointer',
  },
  btnDisabled: { opacity: 0.6, cursor: 'default' },
};

export default IntegrationHealthBanner;
